import { useEffect, useRef, useState } from "react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";

import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { url } from "inspector";
import CategoryModel from "../../models/masters/category-model";
import { SubmitStatus } from "../../core/formik-model-dialog.component";
import { API_END_POINT } from "../../constants";
import { GridCommandCell } from "../../core/grid-command-cell.component";
import { LoadingIndicator } from "../../core/loading-indicator.component";
import {
  DeleteModelDialog,
  DeletePostDataInfo,
  DeleteStatus,
} from "../../core/delete-model-dialog.component";
import AgentModel from "../../models/party/agent-model";
import { useGetAgent } from "../../apiclient/party/useGetAgentApi";
import AgentEntry from "./agent-entry";
import { GridActionComponent } from "../../core/action.title.component";

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const AgentPage: React.FC = () => {
  const initialDataState: State = {
    take: 10,
    skip: 0,
    group: [],
  };
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [deleteagentData, setDeleteAgentData] =
    useState<DeletePostDataInfo>();
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();

  const [editModal, setEditModal] = useState<boolean>(false);
  const [editData, setEditdata] = useState<AgentModel>(null);
  const exportExcel = useRef<ExcelExport | null>(null);
  const [agentShow, setAgentShow] = useState<boolean>(false);

  const [pgreset, setpgreset] = useState<boolean>(false);

  const [viewModal, setViewModal] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: result,
    isLoading,
    isError,
    refetch: reloadAgent,
  } = useGetAgent();

  const processWithGroups = (data: AgentModel[], dataState: State) => {
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };
  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await reloadAgent();
      if (error) {
        console.error("Error reloading:", error);
      } else {
        console.log("Reloaded data:", data);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    setEditdata(null);
    fetchData();
    if (result) {
      setResultData(processWithGroups(result, initialDataState));
    }
  }, [result]);

  let newData: any = null;
  if (resultData) {
    newData = setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    });
  }

  const onSaveComplete = (status: SubmitStatus, data: AgentModel) => {
    setEditdata(null);
    if (status === SubmitStatus.Completed) {
      reloadAgent();
      setResultData(processWithGroups(newData, dataState));
    }
  };

  const excelExport = () => {
    if (exportExcel.current !== null) {
      exportExcel.current.save();
    }
  };

  const onAddNew = () => {
    setEditModal(true);
    setEditdata(new AgentModel());
  };

  const onEdit = (data: AgentModel) => {
    setEditModal(true);
    setViewModal(false);
    setEditdata(data);
  };
  const onView = (data: AgentModel) => {
    setViewModal(true);
    setEditdata(data);
  };
  const onRemove = (data: AgentModel) => {
    const { agentId } = data;
    setDeleteAgentData({
      data,
      route: `Agent/DeleteAgent/${agentId}`,
      endpoint: API_END_POINT.PARTY,
    });
    reloadAgent();
  };
  const onDeleteComplete = (status: DeleteStatus, data: AgentModel) => {
    if (status === DeleteStatus.Completed) {
      reloadAgent();
      setResultData(processWithGroups(result, dataState));
    }
    setDeleteAgentData(null);
  };
  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };

  const CommandCell = (props: GridCellProps) => {
    // Check if the current row is a group header row
    if (props.dataItem.hasOwnProperty("aggregates")) {
      return <td></td>; // Return an empty cell to hide the buttons
    }

    return (
      <GridCommandCell
        {...props}
        edit={onEdit}
        remove={onRemove}
        add={onAddNew}
        dataItem={props.dataItem}
        idPropertyName="agentId"
        editPermissionName={null}
        deletePermissionName={null}
        view={onView}

      />
    );
  };


  const onSearch = (e: any) => {
    if (e.target.value.length > 0) {
      const searchKey: string = e.target.value;
      const filteredData = result?.filter(
        (p) =>
          p?.firstName?.toLowerCase().includes(searchKey?.toLowerCase())
      );
      setResultData(process(filteredData, dataState));
    } else {
      setResultData(process(result, dataState));
    }
  };

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;

    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const refreshGrid = () => {
    reloadAgent();
  };
  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  const ColumnFormatBoolean = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: "black",
        }}
      >
        {props.children === "true" ? (
          <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
        ) : (
          <span style={{ color: "red", fontWeight: "bold" }}>No</span>
        )}
      </td>
    );
  };
  const onAddClick = () => {
    setEditModal(true);
    setViewModal(false);
    setEditdata(new AgentModel());
  };
  let grid = null;
  if (result) {
    grid = (
      <Grid
        data={newData}
        pageable={{ pageSizes: [10, 20, 50, 100] }}
        total={resultData?.total}
        groupable={true}
        sortable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
        columnVirtualization={false}
        resizable={true}
      >
        <GridColumn field="agentCode" title="Code" columnMenu={ColumnMenu} />
        <GridColumn field="firstName" title="First Name" columnMenu={ColumnMenu} />
        <GridColumn field="categoryName" title="Category Name" columnMenu={ColumnMenu} />
        <GridColumn field="aadharNumber" title="Aadhar Number" />
        <GridColumn field="panNumber" title="Pan Number" />
        {/* <GridColumn field="rationcardNumber" title="Ration Card" /> */}
        <GridColumn field="communicationAddress" title="Address" />
        <GridColumn
          field="isActive"
          title="Active ?"
          cells={{ data: ColumnFormatBoolean }}
        />
        <GridColumn
          cell={CommandCell}
          width={"150px"}
          title="Action"
          groupable={true}
        />
        <GridNoRecords>
          <div className="mt-2" style={{ width: "100%", height: "500px" }}>
            <center>
              <h5>No data found</h5>
            </center>
          </div>
        </GridNoRecords>
      </Grid>
    );
    if (isError) {
      return (
        <div>
          <h4>Error Loadong Page..</h4>
        </div>
      );
    }
    return (
      <>

        <div
          style={{ minHeight: "600px" }}
          className="card p-3 border-0 m-3 sw-shadow radius-15"
        >
          <div>
            <GridActionComponent
              onSearchClick={onSearch}
              addButtonClick={onAddClick}
              excelExportClick={excelExport}
              refreshClick={fetchData}
              title="AGENT"
              addButtonTooltip="Add Agent"
            ></GridActionComponent>
          </div>

          {editModal && (
            <AgentEntry isViewMode={viewModal} data={editData} onClose={onSaveComplete} />
          )}
          {viewModal && (
            <AgentEntry
              isViewMode={true}
              data={editData}
              onClose={onSaveComplete}
            />
          )}
          {deleteagentData && (
            <DeleteModelDialog
              title="Delete Agent"
              deleteData={deleteagentData}
              onClose={onDeleteComplete}
            >
              <div>
                Are you sure you want to delete Agent :{" "}
                <span style={{ fontWeight: "bold" }}>
                  {deleteagentData.data.firstName}
                </span>
                ?
              </div>
            </DeleteModelDialog>
          )}

          <div>
            <>
              {isRefreshing || isLoading ? (
                <LoadingIndicator isLoading={isRefreshing}></LoadingIndicator>
              ) : (
                <div>
                  {grid}
                  <ExcelExport
                    fileName={"Agent_List"}
                    data={result}
                    ref={exportExcel}
                  >
                    <ExcelExportColumn width={100} field="agentCode" title="Code #" />
                    <ExcelExportColumn
                      width={100}
                      field="categoryName"
                      title="Category Name"
                    />
                    <ExcelExportColumn
                      width={150}
                      field="firstName"
                      title="First Name"
                    />
                    <ExcelExportColumn
                      width={170}
                      field="lastName"
                      title="Last Name"
                    />
                    <ExcelExportColumn
                      width={150}
                      field="genderName"
                      title="Gender Name"
                    />
                    <ExcelExportColumn
                      width={280}
                      field="nomineeName"
                      title="Nominee Name"
                    />
                    <ExcelExportColumn
                      width={280}
                      field="nomineeRelation"
                      title="Nominee Relation"
                    />
                    <ExcelExportColumn
                      width={170}
                      field="aadharNumber"
                      title="Aadhar Number"
                    />

                    <ExcelExportColumn
                      width={100}
                      field="panNumber"
                      title="PanNumber"
                    />
                    <ExcelExportColumn
                      width={150}
                      field="rationcardNumber"
                      title="Ration Card Number"
                    />
                    <ExcelExportColumn
                      width={170}
                      field="communicationAddress"
                      title="Communication Address"
                    />
                    <ExcelExportColumn width={150} field="email" title="Email" />
                    <ExcelExportColumn
                      width={280}
                      field="pincode"
                      title="Pincode"
                    />
                    <ExcelExportColumn width={280} field="mobile" title="Mobile " />
                    <ExcelExportColumn
                      width={170}
                      field="alternateMobile"
                      title="Alternate Mobile"
                    />
                    <ExcelExportColumn width={170} field="phone" title="Phone" />
                    <ExcelExportColumn
                      width={170}
                      field="ifscCode"
                      title="IFSC Code"
                    />
                    <ExcelExportColumn width={170} field="bank" title="Bank" />
                    <ExcelExportColumn width={170} field="branch" title="Branch" />
                    <ExcelExportColumn
                      width={170}
                      field=" accountHolderName"
                      title=" Account Holder Name"
                    />
                    <ExcelExportColumn
                      width={170}
                      field="accountNumber"
                      title="Account Number"
                    />

                  </ExcelExport>
                </div>
              )}
            </>
          </div>
          <div></div>
        </div>
      </>
    );
  }
};
export default AgentPage;
